import { useRouteMatch } from "react-router-dom";
import sharedStyles from "../styles";
import { usePollData, useQueueSizes, useTaskNames } from "../../data/task";
import { makeStyles } from "@material-ui/styles";
import { Helmet } from "react-helmet";
import { usePushHistory } from "../../components/NavLink";
import { formatRelative } from "date-fns";

import { Paper, DataTable, Dropdown } from "../../components";
import _ from "lodash";
import { timestampRenderer } from "../../utils/helpers";
import { useTabsList } from "../../contexts/TabsContext";
import { Typography } from "@material-ui/core";
import DefaultLoader from "../../components/DefaultLoader";

const useStyles = makeStyles(sharedStyles);

// Local styles scoped for TaskQueue visual refresh
const useTaskQueueStyles = makeStyles((theme) => ({
  pageContainer: {
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    height: "100vh", // Use exact viewport height
    padding: "16px", // Reduced padding
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    overflow: "hidden", // Prevent scrolling on main container
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px", // Reduced padding
    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
    borderRadius: 12, // Reduced border radius
    border: "1px solid rgba(0,0,0,0.08)",
    boxShadow:
      "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
    marginBottom: 16, // Reduced margin
    flexShrink: 0, // Prevent shrinking
  },
  pageTitle: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  pageTitlePrimary: {
    fontSize: 24, // Reduced font size
    fontWeight: 700,
    letterSpacing: -0.5,
    color: "#1e293b",
    lineHeight: 1.2,
  },
  pageTitleSecondary: {
    fontSize: 14, // Reduced font size
    color: "#64748b",
    fontWeight: 400,
  },
  statusIndicator: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 16px",
    borderRadius: 12,
    background: "rgba(34, 197, 94, 0.1)",
    border: "1px solid rgba(34, 197, 94, 0.2)",
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#22c55e",
    boxShadow: "0 0 0 2px rgba(34, 197, 94, 0.2)",
  },
  statusText: {
    fontSize: 14,
    fontWeight: 600,
    color: "#16a34a",
  },
  controlsSection: {
    marginBottom: 16, // Reduced margin
    flexShrink: 0, // Prevent shrinking
  },
  controlsRow: {
    display: "flex",
    gap: 20,
    alignItems: "flex-end",
    flexWrap: "wrap",
  },
  controlCard: {
    padding: "16px 20px", // Reduced padding
    borderRadius: 12, // Reduced border radius
    boxShadow: "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
    border: "1px solid rgba(0,0,0,0.08)",
    background: "#ffffff",
    transition: "all 0.2s ease",
    "&:hover": {
      boxShadow:
        "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
      borderColor: "rgba(59, 130, 246, 0.2)",
    },
  },
  dropdownLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 12,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  tableSection: {
    marginTop: 16, // Reduced margin
    flex: 1, // Allow this section to grow and fill available space
    display: "flex",
    flexDirection: "column",
    minHeight: 0, // Allow shrinking below content size
    overflow: "hidden", // Prevent overflow
  },
  tableCard: {
    padding: 0,
    borderRadius: 12, // Reduced border radius
    boxShadow:
      "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
    border: "1px solid rgba(0,0,0,0.08)",
    background: "#ffffff",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    flex: 1, // Allow the card to fill available space
    minHeight: 0, // Allow shrinking
  },
  tableHeader: {
    padding: "16px 24px", // Reduced padding
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    flexShrink: 0, // Prevent shrinking
  },
  tableTitle: {
    fontSize: 18, // Reduced font size
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: 2, // Reduced margin
  },
  tableSubtitle: {
    fontSize: 13, // Reduced font size
    color: "#64748b",
  },
  tableContent: {
    padding: 12, // Reduced padding
    flex: 1, // Allow content to fill available space
    overflow: "auto", // Allow scrolling within content
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 24px 24px 24px", // Reduced padding significantly
    textAlign: "center",
    background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
    flex: 1, // Fill available space
    overflow: "auto", // Allow scrolling if needed
  },

  emptyStateTitle: {
    fontSize: 20, // Further reduced
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: 4, // Reduced margin
  },
  emptyStateSubtitle: {
    fontSize: 14, // Slightly reduced
    color: "#64748b",
    marginBottom: 16, // Reduced margin
    maxWidth: 350, // Reduced width
    lineHeight: 1.3, // Tighter line height
  },
  emptyStateActions: {
    display: "flex",
    gap: 10, // Reduced gap
    flexWrap: "wrap",
    justifyContent: "center",
  },
  emptyStateButton: {
    padding: "10px 20px", // Reduced padding
    borderRadius: 10, // Reduced border radius
    border: "1px solid rgba(59, 130, 246, 0.2)",
    background: "rgba(59, 130, 246, 0.1)",
    color: "#1d4ed8",
    fontSize: 13, // Reduced font size
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "rgba(59, 130, 246, 0.15)",
      borderColor: "rgba(59, 130, 246, 0.3)",
    },
  },
  loadingOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  loadingCard: {
    padding: 32,
    borderRadius: 16,
    background: "#ffffff",
    boxShadow:
      "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
  },

}));

function getSizesMap(sizes) {
  const retval = new Map();
  for (let row of sizes) {
    if (row.isSuccess) {
      retval.set(row.data.domain, row.data.size);
    }
  }
  return retval;
}

export default function TaskQueue() {
  const taskNames = useTaskNames();
  const pushHistory = usePushHistory();
  const classes = useStyles();
  const tqs = useTaskQueueStyles();
  const match = useRouteMatch();
  const taskName = match.params.name || "";
  const { tabsList } = useTabsList();

  const dropdown_access =
    tabsList?.["Task Queues"]?.task_name_dropdown?.active === "True" &&
    tabsList?.["Task Queues"]?.task_name_dropdown?.hide === "False";

  const { data: pollData, isFetching, refetch } = usePollData(taskName);
  const domains = pollData ? pollData.map((row) => row.domain) : null;
  const sizes = useQueueSizes(taskName, domains);
  const sizesMap = getSizesMap(sizes);
  const now = new Date();

  function setTaskName(name) {
    if (name === null) {
      name = "";
    }
    pushHistory(`/taskQueue/${name}`);
  }

  const hasRows = Array.isArray(pollData) && pollData.length > 0;
  const showEmpty = !isFetching && !hasRows;

  return (
    <div className={tqs.pageContainer}>
      <Helmet>
        <title>Conductor UI - Task Queue</title>
      </Helmet>

      {/* Enhanced Page Header */}
      <div className={tqs.pageHeader}>
        <div className={tqs.pageTitle}>
          <Typography className={tqs.pageTitlePrimary}>
            Task Queue Management
          </Typography>

        </div>
      </div>

      {/* Enhanced Controls Section */}
      {dropdown_access && (
        <div className={tqs.controlsSection}>
          <div className={tqs.controlsRow}>
            <Paper className={tqs.controlCard}>
              <div className={tqs.dropdownLabel}>
                Task Name
              </div>
              <Dropdown
                label="Select a Task Name"
                style={{ width: 420 }}
                options={taskNames}
                onChange={(evt, val) => setTaskName(val)}
                disableClearable
                getOptionSelected={(option, value) => {
                  // Accept empty string
                  if (value === "") return false;
                  return value === option;
                }}
                value={taskName}
              />

            </Paper>
          </div>
        </div>
      )}

      {isFetching && (
        <DefaultLoader
          displayModal={true}
          message={"Fetching latest queue information"}
        />
      )}

      {/* Enhanced Table Section */}
      <div className={tqs.tableSection}>
        {showEmpty && (
          <Paper className={tqs.tableCard}>
            <div className={tqs.emptyState}>
              
              <Typography className={tqs.emptyStateTitle}>
                No Queue Data Available
              </Typography>

              <div className={tqs.emptyStateActions}>
                <button
                  className={tqs.emptyStateButton}
                  onClick={() => refetch()}
                  disabled={!taskName}
                >
                  Refresh Data
                </button>
                {dropdown_access && (
                  <button
                    className={tqs.emptyStateButton}
                    onClick={() => setTaskName("")}
                  >
                    Clear Selection
                  </button>
                )}
              </div>
            </div>
          </Paper>
        )}

        {!_.isUndefined(pollData) && hasRows && (
          <Paper className={tqs.tableCard}>
            <div className={tqs.tableHeader}>
              <Typography className={tqs.tableTitle}>
                Poll Status by Domain
              </Typography>
              <Typography className={tqs.tableSubtitle}>
                Real-time queue metrics and worker activity
                {taskName && ` for task: ${taskName}`}
              </Typography>
            </div>
            <div className={tqs.tableContent}>
              <DataTable
                defaultShowColumns={[
                  "workerId",
                  "domain",
                  "lastPollTime",
                  "queueSize",
                ]}
                default
                data={pollData}
                columns={[
                  {
                    name: "domain",
                    label: "Domain",
                    renderer: (domain) =>
                      _.isEmpty(domain) ? "(Domain not set)" : domain,
                  },
                  { name: "workerId", label: "Last Polled Worker" },
                  {
                    name: "lastPollTime",
                    label: "Last Poll Time",
                    renderer: (time) =>
                      `${timestampRenderer(time)} (${formatRelative(
                        time,
                        now
                      )})`,
                  },
                  {
                    name: "domain",
                    id: "queueSize",
                    label: "Queue Size",
                    renderer: (domain) => {
                      const size = sizesMap.get(domain);
                      return size !== undefined ? size : "—";
                    },
                  },
                ]}
              />
            </div>
          </Paper>
        )}
      </div>
    </div>
  );
}
